/** Hilo in-app/portal: mensaje inicial + thread + encuesta (orden cronológico). */

import type { FeedbackAttachment } from './feedbackAttachments';

export type SupportThreadMessage = {
  id: string;
  author: 'staff' | 'commerce';
  body: string;
  staff_label: string | null;
  created_at: string;
  attachments?: FeedbackAttachment[];
};

export type SupportTicketSurveyFields = {
  user_resolution: string | null;
  user_resolution_at: string | null;
  user_resolution_note: string | null;
};

export type TimelineEntry =
  | { kind: 'commerce_initial'; created_at: string; body: string; attachments?: FeedbackAttachment[] }
  | { kind: 'thread'; message: SupportThreadMessage; showReadMeta: boolean }
  | {
      kind: 'survey';
      at: string;
      user_resolution: string;
      user_resolution_note: string | null;
    };

const LEGACY_STAFF_ID = '00000000-0000-4000-8000-000000000001';

export function formatSupportSurveyThreadBody(
  resolution: string,
  note: string | null | undefined
): string {
  const trimmed = note?.trim() ?? '';
  if (trimmed.length > 0) return trimmed;
  if (resolution === 'ok') return 'Indicó que la respuesta le resolvió el tema.';
  return 'Indicó que aún necesita seguimiento.';
}

function sameInstant(a: string, b: string): boolean {
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (Number.isFinite(da) && Number.isFinite(db)) return da === db;
  return a === b;
}

/** La encuesta ya está copiada al hilo: no duplicar el bloque en el timeline. */
export function surveyIsArchivedInThread(
  messages: SupportThreadMessage[],
  ticket: Pick<SupportTicketSurveyFields, 'user_resolution_at'>
): boolean {
  if (!ticket.user_resolution_at) return false;
  return messages.some(
    (m) => m.author === 'commerce' && sameInstant(m.created_at, ticket.user_resolution_at as string)
  );
}

/** Si no hay filas en thread (migración pendiente), usar public_reply como único mensaje staff. */
export function withLegacyStaffMessages(
  messages: SupportThreadMessage[],
  ticket: { public_reply: string | null; replied_at: string | null; replied_by: string | null }
): SupportThreadMessage[] {
  if (messages.length > 0) {
    return messages;
  }
  if (ticket.public_reply?.trim() && ticket.replied_at) {
    return [
      {
        id: LEGACY_STAFF_ID,
        author: 'staff',
        body: ticket.public_reply,
        staff_label: ticket.replied_by,
        created_at: ticket.replied_at,
      },
    ];
  }
  return [];
}

export function buildSupportTimeline(
  ticket: {
    body: string;
    created_at: string;
    read_by_user_at: string | null;
    initial_attachments?: FeedbackAttachment[];
  } & SupportTicketSurveyFields & {
      public_reply: string | null;
      replied_at: string | null;
      replied_by: string | null;
    },
  rawMessages: SupportThreadMessage[]
): TimelineEntry[] {
  const messages = withLegacyStaffMessages(rawMessages, ticket);

  const staffSorted = messages
    .filter((m) => m.author === 'staff')
    .sort((a, b) => {
      if (a.created_at < b.created_at) {
        return -1;
      }
      if (a.created_at > b.created_at) {
        return 1;
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  const lastStaff = staffSorted.at(-1);

  type SortRow = {
    order: 0 | 1 | 2;
    t: string;
    key: string;
    entry: TimelineEntry;
  };

  const rows: SortRow[] = [
    {
      order: 0,
      t: ticket.created_at,
      key: 'init',
      entry: {
        kind: 'commerce_initial',
        created_at: ticket.created_at,
        body: ticket.body,
        attachments: ticket.initial_attachments,
      },
    },
  ];

  for (const m of messages) {
    const showReadMeta = Boolean(lastStaff && m.author === 'staff' && m.id === lastStaff.id);
    rows.push({
      order: 1,
      t: m.created_at,
      key: m.id,
      entry: { kind: 'thread', message: m, showReadMeta },
    });
  }

  if (
    ticket.user_resolution &&
    ticket.user_resolution_at &&
    !surveyIsArchivedInThread(messages, ticket)
  ) {
    rows.push({
      order: 2,
      t: ticket.user_resolution_at,
      key: 'survey',
      entry: {
        kind: 'survey',
        at: ticket.user_resolution_at,
        user_resolution: ticket.user_resolution,
        user_resolution_note: ticket.user_resolution_note,
      },
    });
  }

  rows.sort((a, b) => {
    if (a.t < b.t) {
      return -1;
    }
    if (a.t > b.t) {
      return 1;
    }
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  });

  return rows.map((r) => r.entry);
}
